import React, { useState } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Alert,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { AppDispatch } from "../../store/store";
import { saveCoAgentFilter } from "../../store/modules/coAgentFilter/coAgentFilterActions";
import LoadingDialog from "../Dialogs/LoadingDialog";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface TeamMemberFilterSettingsProps {}

const TeamMemberFilterSettings: React.FC<
  TeamMemberFilterSettingsProps
> = ({}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedCoAgentIds, saving } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );
  const { data: userData } = useSelector(
    (state: RootState) => state.commonData.userData,
  );

  const coAgents = userData?.coAgents || [];
  const [localSelection, setLocalSelection] =
    useState<number[]>(selectedCoAgentIds);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  const handleToggle = (coAgentId: number) => {
    setLocalSelection((prev) => {
      if (prev.includes(coAgentId)) {
        return prev.filter((id) => id !== coAgentId);
      } else {
        return [...prev, coAgentId];
      }
    });
  };

  const handleSelectAll = () => {
    setLocalSelection(coAgents.map((ca) => ca.id!).filter((id) => id));
  };

  const handleClearAll = () => {
    setLocalSelection([]);
  };

  const handleSave = () => {
    dispatch(saveCoAgentFilter(localSelection));
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  const hasChanges =
    JSON.stringify([...localSelection].sort()) !==
    JSON.stringify([...selectedCoAgentIds].sort());

  const selectedCount = localSelection.length;
  const totalCount = coAgents.length;

  return (
    <>
      <LoadingDialog open={saving} />
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 2px 8px 0 rgba(0,0,0,0.04)",
          p: { xs: 2, md: 4 },
          mb: 1,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
            pb: 2,
            borderBottom: "2px solid #f0f0f0",
          }}
        >
          <FilterListIcon
            sx={{ color: "primary.main", mr: 1.5, fontSize: 28 }}
          />
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, color: "#222", flexGrow: 1 }}
          >
            {selectedCount === 0
              ? "Showing all team members across the system"
              : `Filtering by ${selectedCount} of ${totalCount} team member${selectedCount === 1 ? "" : "s"}`}
          </Typography>
          {selectedCount > 0 && (
            <CheckCircleIcon sx={{ color: "success.main", fontSize: 28 }} />
          )}
        </Box>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          This filter applies globally across all pages and analytics. Select
          team members to show only their data, or leave empty to show all data.
        </Alert>

        {/* Save Success Alert */}
        {showSaveSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Filter settings saved successfully!
          </Alert>
        )}

        {/* Quick Actions */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleSelectAll}
            disabled={selectedCount === totalCount}
          >
            Select All
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearAll}
            disabled={selectedCount === 0}
          >
            Clear All
          </Button>
        </Box>

        {/* Team Members List */}
        <Box sx={{ mb: 3 }}>
          {coAgents.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: "#999", fontStyle: "italic" }}
            >
              No team members available
            </Typography>
          ) : (
            coAgents.map((coAgent) => {
              const isSelected = localSelection.includes(coAgent.id!);
              return (
                <Box
                  key={coAgent.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    background: isSelected ? "#E3F2FD" : "#F7F9FB",
                    borderRadius: "8px",
                    px: 2,
                    py: 1,
                    mb: 1.5,
                    border: (theme) =>
                      isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : "2px solid transparent",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: isSelected ? "#BBDEFB" : "#EEEFF1",
                    },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggle(coAgent.id!)}
                        sx={{
                          color: "primary.main",
                          "&.Mui-checked": {
                            color: "primary.main",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontWeight: isSelected ? 600 : 500,
                          color: isSelected ? "primary.dark" : "#222",
                          fontSize: "1rem",
                        }}
                      >
                        {coAgent.name}
                      </Typography>
                    }
                    sx={{ flexGrow: 1, m: 0 }}
                  />
                </Box>
              );
            })
          )}
        </Box>

        {/* Save Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            pt: 2,
            borderTop: "2px solid #f0f0f0",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!hasChanges || saving}
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 600,
              fontSize: "1rem",
            }}
          >
            {saving ? "Saving..." : "Save Filter"}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default TeamMemberFilterSettings;
